/**
 * Branchement des écrans de réglages comptables et de la clôture annuelle sur le contexte applicatif
 * (stockage par entreprise, journal d'audit, confirmations, notifications).
 */
import type { AppContext } from '@application/context/AppContext';
import { recordAuditEvent } from '@application/audit/AuditDirectoryService';
import { loadComptesPaie, saveComptesPaie } from '@application/comptabilite/ComptesPaieStore';
import { loadComptesTva, saveComptesTva } from '@application/comptabilite/ComptesTvaStore';
import { loadBaremeTva, saveBaremeTva } from '@application/comptabilite/BaremeTvaStore';
import { loadPeriodesCloturees } from '@application/comptabilite/PeriodesCloturees';
import {
  cloturerExerciceAtomique,
  type ClotureAnnuelleInput,
  type DepsCloture,
} from '@application/comptabilite/CloturePeriode';
import { depsClotureDepuisContexte } from '@application/comptabilite/CloturePeriodeDeps';
import {
  enregistrerStatutExercice,
  exerciceClotureDansRegistre,
  restaurerStatutExercice,
  type ExerciceRegistre,
} from '@application/comptabilite/RegistreExercices';
import {
  exerciceFiscalDepuisAnnee,
  suggererParametresCloture,
} from '@application/comptabilite/PreparationClotureAnnuelle';
import { loadRegister, saveRegister } from '@presentation/shell/localCrud';
import type { Immobilisation } from '@shared/types/Immobilisation';
import { confirmerDialogue, notifierToast } from './panelKit';
import { mountComptesPaiePanel } from './ComptesPaiePanel';
import { mountComptesTvaPanel } from './ComptesTvaPanel';
import { mountBaremeTvaPanel } from './BaremeTvaPanel';
import { ouvrirClotureAnnuelle, type ClotureAnnuelleDeps } from './ClotureAnnuellePanel';

const auditParametres =
  (ctx: AppContext) =>
  (description: string, payload?: Record<string, unknown>): Promise<void> =>
    recordAuditEvent(ctx, {
      action: 'update',
      entity: 'parametres',
      description,
      ...(payload ? { payload } : {}),
    });

export async function monterReglagesComptables(
  section: HTMLElement,
  ctx: AppContext,
  tenantId: string,
  canMutate: boolean,
): Promise<void> {
  const audit = auditParametres(ctx);
  const hotePaie = section.querySelector<HTMLElement>('#cpt-reglages-paie');
  const hoteTva = section.querySelector<HTMLElement>('#cpt-reglages-tva');
  const hoteBareme = section.querySelector<HTMLElement>('#cpt-reglages-bareme');
  const communs = { canMutate, audit, confirm: confirmerDialogue, notify: notifierToast };
  await Promise.all([
    hotePaie
      ? mountComptesPaiePanel(hotePaie, {
          ...communs,
          load: () => loadComptesPaie(tenantId),
          save: (s) => saveComptesPaie(tenantId, s),
        })
      : undefined,
    hoteTva
      ? mountComptesTvaPanel(hoteTva, {
          ...communs,
          load: () => loadComptesTva(tenantId),
          save: (s) => saveComptesTva(tenantId, s),
        })
      : undefined,
    hoteBareme
      ? mountBaremeTvaPanel(hoteBareme, {
          ...communs,
          load: () => loadBaremeTva(tenantId),
          save: (a) => saveBaremeTva(tenantId, a),
        })
      : undefined,
  ]);
}

/** Dépendances de la clôture annuelle : ports de clôture + persistance de l'exercice dans le registre. */
export function depsClotureAnnuelle(ctx: AppContext, tenantId: string): DepsCloture {
  let avant: ExerciceRegistre | undefined;
  return {
    ...depsClotureDepuisContexte(ctx, tenantId),
    saveExercice: async (exercice) => {
      const registre = await loadRegister<ExerciceRegistre>(tenantId, 'exercices');
      avant = registre.find((e) => e.annee === exercice.code && !e.id.startsWith('derived-'));
      await saveRegister(tenantId, 'exercices', enregistrerStatutExercice(registre, exercice));
    },
    restoreExercice: async (exercice) => {
      const registre = await loadRegister<ExerciceRegistre>(tenantId, 'exercices');
      await saveRegister(tenantId, 'exercices', restaurerStatutExercice(registre, exercice.code, avant));
    },
  };
}

export function ouvrirAssistantClotureAnnuelle(
  section: HTMLElement,
  ctx: AppContext,
  tenantId: string,
  canMutate: boolean,
  apres: () => Promise<void> | void,
): void {
  const tenant = ctx.tenantContext.tryGetTenant();
  const courante = tenant?.exercice_courant ?? String(new Date().getFullYear());
  void (async () => {
    const [ecritures, registre] = await Promise.all([
      ctx.ecritures.list(),
      loadRegister<ExerciceRegistre>(tenantId, 'exercices'),
    ]);
    const annees = new Set<string>([courante]);
    for (const e of ecritures) if (/^\d{4}-/.test(e.periode)) annees.add(e.periode.slice(0, 4));
    const aClore = [...annees].filter((a) => !exerciceClotureDansRegistre(registre, a)).sort((a, b) => b.localeCompare(a));
    const liste = aClore.length > 0 ? aClore : [courante];
    const deps: ClotureAnnuelleDeps = {
      peutCloturer: canMutate,
      annees: liste,
      anneeInitiale: liste.includes(courante) ? courante : (liste[0] ?? courante),
      charger: async (annee) => {
        const [ecr, bulletins, immobilisations, cloturees, reg] = await Promise.all([
          ctx.ecritures.list(),
          ctx.bulletins.list(),
          loadRegister<Immobilisation>(tenantId, 'immobilisations_comptables'),
          loadPeriodesCloturees(tenantId),
          loadRegister<ExerciceRegistre>(tenantId, 'exercices'),
        ]);
        const cloture = exerciceClotureDansRegistre(reg, annee);
        const ligne = reg.find((e) => e.annee === annee && !e.id.startsWith('derived-'));
        return {
          exercice: exerciceFiscalDepuisAnnee(tenantId, annee, cloture ? 'cloture' : 'ouvert', ligne?.id ?? `exo-${annee}`),
          ecritures: ecr,
          immobilisations,
          params: suggererParametresCloture({
            annee,
            ecritures: ecr,
            bulletins,
            capitalSocial: tenant?.capital_social ?? 0,
          }),
          cloturees,
          exerciceCloture: cloture,
        };
      },
      executer: (input: ClotureAnnuelleInput) => cloturerExerciceAtomique(depsClotureAnnuelle(ctx, tenantId), input),
      confirm: confirmerDialogue,
      notify: notifierToast,
      apres,
    };
    ouvrirClotureAnnuelle(section, deps);
  })();
}
